import classNames from 'classnames';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { first } from 'lodash';

import { isEmptyExploration } from '@/core/exploration';
import {
  getConversationCells,
  isChatCell,
  isConversationCell,
  isConversationPart,
  isEndOfConversation,
  isEndOfReply,
  isStartOfConversation,
  isStartOfReply,
} from '@/core/cell';

import {
  Cell,
  ChatMode,
  ChatSource,
  Conversation,
  Exploration,
  ExplorationParameters,
} from '@/explore/types';
import { CopiedCell } from '@/explore/utils/use-persist-cell';
import { getAbsoluteCellIndex, getVariableDefinitions } from '@/explore/utils';
import { ExplorationSearchModal } from '@/explore/exploration-search';
import { notNil } from '@/lib/utils';
import { Breakpoint, useScreenSize } from '@/lib/hooks';
import { AnalyticsContextProvider } from '@/lib/analytics';
import { HideInEmbedded } from '@/components/layout/hide-in-embedded';
import { useConversationExplorationContext } from '@/explore/conversation/conversation-exploration-context';

import { CellInput } from '../cell-input';
import { ConversationInput } from '../conversation-cell/conversation-input';
import { CellDropZone } from '../exploration-layout/cell-dropzone';
import { getCellRows } from '../exploration-layout/utils';
import { CellInputTrigger } from '../cell-input/cell-input-trigger';
import { ExplorationCell } from '../exploration-cell';
import { ScrollToSelected } from '../scroll-to-selected';
import { ConversationLink } from '../../conversation/conversation-link';

import { useExplorationContext } from '../exploration-context';

import layoutStyles from '../exploration-layout/exploration-layout.module.scss';
import notebookStyles from './exploration-notebook.module.scss';

type ExplorationNotebookProps = {
  exploration: Exploration;
  setExploration: (exploration: Exploration) => void;
  parameters: ExplorationParameters;
  selectCell: (cellId: string) => void;
  deselectCell: () => void;
  scrollToCell: (cellId: string | null) => void;
  showDataCatalog: boolean;
  setShowDataCatalog: (show: boolean) => void;
  searchTerm: string;
  setSearchTerm: (query: string) => void;
  newCellIndex: number | null;
  setNewCellIndex: (index: number | null) => void;
  newConversationSubjectId: string | null;
  setNewConversationSubjectId: (cellId: string | null) => void;
  isEditorOpen: boolean;
  closeEditor: () => void;
  openEditor: (cellId: string) => void;
  selectedCell: Cell | null;
  scrollToId: string | null;
  highlightedCellId: string | null;
  embeddedMode: boolean;
  onAsk: (
    searchQuery: string,
    sources: string[],
    mode: ChatMode,
    index: number,
  ) => Promise<{ conversation: Conversation | null; error: Error | undefined }>;
  onExplorationClick: (exploration: Exploration, index: number) => void;
  copiedCell: CopiedCell | undefined;
  onCellPaste: (cell: CopiedCell | undefined, index: number) => void;
  onBlockAdd: (index: number) => void;
  cellsRef: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
};

export const ExplorationNotebook = (props: ExplorationNotebookProps) => {
  const {
    exploration,
    parameters,
    showDataCatalog,
    setShowDataCatalog,
    newCellIndex,
    setNewCellIndex,
    newConversationSubjectId,
    setNewConversationSubjectId,
    searchTerm,
    setSearchTerm,
    isEditorOpen,
    closeEditor,
    openEditor,
    selectedCell,
    scrollToId,
    highlightedCellId,
    scrollToCell,
    embeddedMode,
    onAsk,
    onExplorationClick,
    copiedCell,
    onCellPaste,
    onBlockAdd,
    cellsRef,
  } = props;

  const isInitialLoadRef = useRef(scrollToId !== null);
  const [explorationRowHeights, setExplorationRowHeights] = useState<(number | undefined)[]>([]);
  const { isWithinConversation } = useConversationExplorationContext();
  const { isConversationSubject } = useExplorationContext();
  const screenSize = useScreenSize();

  const emptyExploration = isEmptyExploration(exploration);
  const desktopView = screenSize.breakpoint > Breakpoint.md;

  const rows = useMemo(() => getCellRows(exploration.view.cells), [exploration.view.cells]);

  const handleShowCatalog = (query: string) => {
    setSearchTerm(query);
    setShowDataCatalog(true);
  };

  useEffect(() => {
    if (scrollToId === null) {
      return;
    }

    // For a maximum of 2 seconds, try to keep the <scrollToId> cell within the viewport.
    // This way if preceding blocks finish loading with way fewer items than their loading state's height,
    // the layout shift doesn't cause the <scrollToId> cell to go out of the user's viewport.
    const MaxAttempts = 60; // ~1 second at 60fps
    let cancelled = false;
    let attempts = 0;
    let hasScrolled = false;
    let observer: IntersectionObserver | null = null;
    let stabilityTimer: ReturnType<typeof setTimeout> | null = null;

    const tryScroll = () => {
      if (cancelled) {
        return;
      }

      const scrollToCellEl = document.querySelector(`[data-id="${scrollToId}"]`);

      if (scrollToCellEl !== null) {
        scrollToCellEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        hasScrolled = true;

        // Only stabilize scroll on initial load, when content is still settling
        if (isInitialLoadRef.current) {
          observer = new IntersectionObserver(
            (entries) => {
              if (cancelled) {
                return;
              }

              const entry = entries[0];
              // If element is not fully visible and we've already scrolled once
              if (hasScrolled && entry.intersectionRatio < 0.9) {
                scrollToCellEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            },
            { threshold: [0, 0.5, 0.9, 1] },
          );

          observer.observe(scrollToCellEl);

          stabilityTimer = setTimeout(() => {
            if (observer) {
              observer.disconnect();
            }
            isInitialLoadRef.current = false;
            scrollToCell(null);
          }, 2000);
        } else {
          scrollToCell(null);
        }
      } else if (attempts < MaxAttempts) {
        attempts++;
        requestAnimationFrame(tryScroll);
      } else {
        console.warn('Could not find element to scroll to:', scrollToId);
        scrollToCell(null);
      }
    };

    requestAnimationFrame(tryScroll);

    return () => {
      isInitialLoadRef.current = false;
      cancelled = true;
      if (observer) {
        observer.disconnect();
      }
      if (stabilityTimer) {
        clearTimeout(stabilityTimer);
      }
    };
  }, [scrollToCell, scrollToId]);

  const setRowHeightOverride = (index: number, height: number | undefined) => {
    setExplorationRowHeights((prev) => {
      const next = [...prev];
      next[index] = height;
      return next;
    });
  };

  const precededByConversationCell = (row: Cell[], rowIndex: number) => {
    const cell = row.find((c) => isConversationPart(c));
    if (cell === undefined) {
      return false;
    }

    const previousCell = rowIndex > 0 ? rows[rowIndex - 1][0] : null;

    return (
      notNil(previousCell) &&
      isConversationPart(previousCell) &&
      previousCell.conversationId === cell.conversationId
    );
  };

  const followedByConversationCell = (row: Cell[], rowIndex: number) => {
    const cell = row.find((c) => isConversationPart(c));
    if (cell === undefined) {
      return false;
    }

    const nextCell = rowIndex < rows.length - 1 ? rows[rowIndex + 1][0] : null;

    return (
      notNil(nextCell) &&
      (isConversationPart(nextCell) || isConversationCell(nextCell)) &&
      nextCell.conversationId === cell.conversationId
    );
  };

  const belongsToLastResponseGroup = (cellToCheck: Cell) => {
    if (!isConversationPart(cellToCheck)) {
      return false;
    }

    const cells = getConversationCells(exploration.view.cells, cellToCheck.conversationId);
    const group: string[] = [];

    if (cells.some(isConversationCell)) {
      return false;
    }

    for (const cell of cells.slice().reverse()) {
      const chatCell = isChatCell(cell);
      if (!chatCell) {
        group.push(cell.id);
      } else if (chatCell && group.length === 0) {
        continue;
      } else if (chatCell && group.length) {
        break;
      }
    }

    return group.includes(cellToCheck.id);
  };

  const renderExplorationList = (index: number) => (
    <AnalyticsContextProvider
      properties={{
        intent: 'new block',
        exploration,
        atIndex: index,
      }}>
      <ExplorationSearchModal
        exploration={!emptyExploration ? exploration : undefined}
        selectIndividualCells={!emptyExploration}
        copiedCell={copiedCell}
        defaultSearchTerm={searchTerm}
        onClose={() => setShowDataCatalog(false)}
        onClickExploration={(exploration) => {
          setNewCellIndex(null);
          setShowDataCatalog(false);
          if (!emptyExploration) {
            onExplorationClick(exploration, index);
            return false;
          }
        }}
        onPasteExploration={() => copiedCell !== undefined && onCellPaste(copiedCell, index)}
      />
    </AnalyticsContextProvider>
  );

  return (
    <div ref={cellsRef} className={classNames(emptyExploration && notebookStyles.withTitle)}>
      <ScrollToSelected
        direction="up"
        cellId={selectedCell?.id}
        onClick={() => scrollToCell(selectedCell?.id ?? null)}
      />

      {isEditorOpen && <div className={notebookStyles.overlay} onClick={closeEditor} />}

      {emptyExploration ? (
        <CellInput
          emptyExploration={emptyExploration}
          onAsk={(search, sources, mode) => onAsk(search, sources, mode, 0)}
          onShowCatalog={handleShowCatalog}
          onClickExploration={(exploration) => onExplorationClick(exploration, 0)}
          onClose={() => setNewCellIndex(null)}
        />
      ) : newCellIndex !== 0 ? (
        <CellDropZone index={0} addBefore mergeIntoRow={false}>
          <HideInEmbedded renderHidden>
            <CellInputTrigger
              compact={false}
              showPaste={copiedCell !== undefined}
              onClick={() => onBlockAdd(0)}
              onPaste={() => onCellPaste(copiedCell, 0)}
            />
          </HideInEmbedded>
        </CellDropZone>
      ) : (
        <CellInput
          onAsk={(search, sources, mode) => onAsk(search, sources, mode, 0)}
          onShowCatalog={handleShowCatalog}
          onClickExploration={(exploration) => onExplorationClick(exploration, 0)}
          onClose={() => setNewCellIndex(null)}
        />
      )}

      {showDataCatalog && (newCellIndex === 0 || newCellIndex === null) && renderExplorationList(0)}
      {rows.map((cells, rowIndex) => {
        const nextRowCellIndex = getAbsoluteCellIndex(rows, rowIndex + 1, 0);
        const rowKey = first(cells)?.viewOptions?.rowId ?? first(cells)?.id ?? rowIndex;
        const isLastRow = rowIndex === rows.length - 1;
        const [firstCell] = cells;
        const rowContainsConversation = cells.some((cell) => isConversationCell(cell));
        const isFirstConversationRow =
          rowContainsConversation && !precededByConversationCell(cells, rowIndex);
        const isLastConversationRow = !followedByConversationCell(cells, rowIndex);
        const isLastConversationReply = belongsToLastResponseGroup(firstCell);
        const rowContainsSubject = cells.some(isConversationSubject);
        const isPartOfConversation = !rowContainsConversation && isConversationPart(firstCell);
        const isStartOfConversationReply = isStartOfReply(firstCell, exploration.view.cells);
        const isEndOfConversationReply = cells.some((cell) =>
          isEndOfReply(cell, exploration.view.cells),
        );
        const isConversationStart =
          isPartOfConversation && isStartOfConversation(firstCell, exploration.view.cells);
        const isConversationEnd =
          isPartOfConversation && isEndOfConversation(firstCell, exploration.view.cells);
        const isFollowupChatCell = isChatCell(firstCell) && !isConversationStart;
        const showConversationInput = cells.some((cell) => cell.id === newConversationSubjectId);

        return (
          <Fragment key={`${rowKey}-${firstCell.id}`}>
            <div
              className={classNames(layoutStyles.explorationRow, {
                [notebookStyles.conversationRow]:
                  !rowContainsSubject &&
                  !isWithinConversation &&
                  isPartOfConversation &&
                  !isChatCell(firstCell),
                [notebookStyles.conversationRowStart]:
                  !rowContainsSubject &&
                  !isWithinConversation &&
                  isPartOfConversation &&
                  !isChatCell(firstCell) &&
                  isStartOfConversationReply,
                [notebookStyles.conversationRowEnd]:
                  !rowContainsSubject &&
                  !isWithinConversation &&
                  isPartOfConversation &&
                  !isChatCell(firstCell) &&
                  isEndOfConversationReply,
                [notebookStyles.conversationLine]:
                  (rowContainsConversation && !isFirstConversationRow) ||
                  (!rowContainsConversation &&
                    !isChatCell(firstCell) &&
                    isStartOfConversationReply &&
                    !isConversationStart) ||
                  isFollowupChatCell,
                [notebookStyles.conversationActive]: isLastConversationReply || isConversationEnd,
                [notebookStyles.conversationSubject]: rowContainsSubject,
              })}>
              {cells.map((cell, i) => {
                const cellIndex = getAbsoluteCellIndex(rows, rowIndex, i);
                const isSelected = cell.id === selectedCell?.id;
                const cellIsConversationSubject = isConversationSubject(cell);
                const conversationSubject = cellIsConversationSubject
                  ? cell
                  : isConversationCell(cell)
                    ? exploration.view.cells.find(
                        (c) =>
                          isConversationPart(c) &&
                          !isConversationCell(c) &&
                          c.conversationId === cell.conversationId,
                      )
                    : undefined;

                return (
                  <Fragment key={cell.id}>
                    <ExplorationCell
                      index={cellIndex}
                      parameters={parameters}
                      cell={cell}
                      selected={isSelected}
                      highlighted={cell.id === highlightedCellId}
                      height={cell.viewOptions?.height}
                      rowHeightOverride={explorationRowHeights[rowIndex]}
                      setRowHeightOverride={(height) => setRowHeightOverride(rowIndex, height)}
                      variables={getVariableDefinitions(exploration)}
                      isResizable={!(embeddedMode || isConversationCell(firstCell))}
                      isCollapsible
                      onSelectCell={() => !isSelected && desktopView && openEditor(cell.id)}
                    />
                    {i < cells.length - 1 && (!isConversationPart(cell) || cells.length > 1) && (
                      <CellDropZone
                        index={cellIndex}
                        vertical
                        mergeIntoRow
                        className={layoutStyles.cellSeparator}
                      />
                    )}
                    {cellIsConversationSubject && conversationSubject !== undefined && (
                      <ConversationLink
                        conversationSubjectIndex={i}
                        row={cells}
                        active={showConversationInput}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>

            {showConversationInput && (
              <ConversationInput
                sources={[ChatSource.Database]}
                onCancel={() => setNewConversationSubjectId(null)}
                onSubmit={(search, sources, mode) => onAsk(search, sources, mode, nextRowCellIndex)}
              />
            )}

            <HideInEmbedded renderHidden>
              {newCellIndex !== nextRowCellIndex && isLastConversationRow ? (
                <CellDropZone index={nextRowCellIndex - 1} mergeIntoRow={false}>
                  <CellInputTrigger
                    compact={!isLastRow}
                    showPaste={copiedCell !== undefined}
                    onClick={() => onBlockAdd(nextRowCellIndex)}
                    onPaste={() => onCellPaste(copiedCell, nextRowCellIndex)}
                  />
                </CellDropZone>
              ) : newCellIndex === nextRowCellIndex ? (
                <CellInput
                  onAsk={(search, sources, mode) => onAsk(search, sources, mode, nextRowCellIndex)}
                  onShowCatalog={handleShowCatalog}
                  onClickExploration={(exploration) =>
                    onExplorationClick(exploration, nextRowCellIndex)
                  }
                  onClose={() => setNewCellIndex(null)}
                />
              ) : null}
            </HideInEmbedded>

            {showDataCatalog &&
              newCellIndex === nextRowCellIndex &&
              renderExplorationList(nextRowCellIndex)}
          </Fragment>
        );
      })}
      <ScrollToSelected
        direction="down"
        cellId={selectedCell?.id}
        onClick={() => scrollToCell(selectedCell?.id ?? null)}
      />
    </div>
  );
};
